import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { AnimatePresence, motion } from 'framer-motion'
import { ArrowUp, CalendarClock, LoaderCircle, ScanSearch, Search, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { postMemoryQuery } from './memoryQuery'
import type { MemoryCopy, MemoryRecallPayload, MemoryRow } from './types'
import { formatMemoryDate } from './utils'

interface Props {
  workerId: string
  copy: MemoryCopy
  refreshKey: number
  resetKey: number
  onSelect: (row: MemoryRow) => void
  onPreview: (row: MemoryRow) => void
  onSearch: (query: string) => void
  onClearSearch: () => void
}

export default function MemoryRecallMode({ workerId, copy, refreshKey, resetKey, onSelect, onPreview, onSearch, onClearSearch }: Props) {
  const [query, setQuery] = useState('')
  const [payload, setPayload] = useState<MemoryRecallPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const requestRef = useRef<AbortController | null>(null)
  const previousRefreshRef = useRef(refreshKey)
  const previousResetRef = useRef(resetKey)

  const searchMemory = useCallback(async () => {
    if (!query.trim()) {
      setError(copy.recall.invalid)
      return
    }

    requestRef.current?.abort()
    const controller = new AbortController()
    requestRef.current = controller
    setLoading(true)
    setError(null)
    setPayload(null)
    onSearch(query.trim())

    try {
      const result = await postMemoryQuery<MemoryRecallPayload>(
        `/dashboard/digital_workers/${workerId}/memory_recall`,
        { query: query.trim() },
        controller.signal,
      )
      if (!result.ok) {
        setError(result.data.status === 'unavailable' ? copy.recall.unavailable : result.data.status === 'invalid' ? copy.recall.invalid : copy.recall.error)
        return
      }
      setPayload(result.data)
    } catch (requestError) {
      if ((requestError as Error).name !== 'AbortError') setError(copy.recall.error)
    } finally {
      if (requestRef.current === controller) setLoading(false)
    }
  }, [copy.recall, onSearch, query, workerId])

  useEffect(() => () => requestRef.current?.abort(), [])
  useEffect(() => {
    if (previousRefreshRef.current === refreshKey) return
    previousRefreshRef.current = refreshKey
    if (payload && query.trim()) void searchMemory()
  }, [payload, query, refreshKey, searchMemory])
  useEffect(() => {
    if (previousResetRef.current === resetKey) return
    previousResetRef.current = resetKey
    requestRef.current?.abort()
    setQuery('')
    setPayload(null)
    setError(null)
    setLoading(false)
  }, [resetKey])

  const rows = payload ? normalizeRecallRows(payload) : []

  const clearSearch = () => {
    requestRef.current?.abort()
    setQuery('')
    setPayload(null)
    setError(null)
    setLoading(false)
    onClearSearch()
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <form onSubmit={(event) => { event.preventDefault(); void searchMemory() }} className="border-b border-border p-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 z-10 size-4 -translate-y-1/2 text-muted-foreground transition-colors" />
          <Input
            type="text"
            role="searchbox"
            aria-label={copy.recall.query_label}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={copy.recall.query_placeholder}
            className="h-11 rounded-xl border-border/80 bg-muted/20 pl-10 pr-[5rem] text-sm shadow-none transition-[background-color,border-color] hover:bg-muted/30 focus-visible:border-muted-foreground/45 focus-visible:bg-background focus-visible:ring-0"
          />
          <AnimatePresence initial={false}>
            {(query || payload) && (
              <motion.div className="absolute right-11 top-1.5" initial={{ opacity: 0, scale: 0.72 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.72 }} transition={{ duration: 0.14 }}>
                <Button type="button" variant="ghost" size="icon-sm" onClick={clearSearch} aria-label={copy.filters.clear} className="size-8"><X /></Button>
              </motion.div>
            )}
          </AnimatePresence>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="submit" size="icon-sm" disabled={loading || !query.trim()} aria-label={copy.recall.submit} className="absolute right-1 top-1 size-9 rounded-lg">
                {loading ? <LoaderCircle className="animate-spin" /> : <ArrowUp />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" sideOffset={6}>{copy.recall.submit}</TooltipContent>
          </Tooltip>
        </div>
      </form>

      <ScrollArea className="min-h-0 flex-1">
        <div className="p-3">
          {!payload && !loading && !error && <EmptyRecall title={copy.inspector.recall} description={copy.recall.ready_description} />}
          {loading && <SearchStatus icon={<LoaderCircle className="size-5 animate-spin text-primary" />} title={copy.recall.loading} />}
          {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
          {!loading && payload && rows.length === 0 && <EmptyRecall title={copy.recall.no_results} description={`“${query.trim()}”`} />}

          {!loading && rows.length > 0 && (
            <div data-testid="memory-recall-results" className="space-y-1">
              <p className="px-2 pb-1 text-[10.5px] text-faint-foreground">{copy.recall.result_count.replace('%{count}', String(rows.length))}</p>
              {rows.map((row) => <RecallCard key={row.id} row={row} copy={copy} onPreview={onPreview} onSelect={onSelect} />)}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}

function normalizeRecallRows(payload: MemoryRecallPayload): MemoryRow[] {
  return (payload.results || []).map((result) => ({
    ...result,
    fact_type: result.type || result.fact_type,
    entities: result.entities?.join(', ') || null,
  }))
}

function RecallCard({ row, copy, onPreview, onSelect }: { row: MemoryRow & { score?: number | null }; copy: MemoryCopy; onPreview: (row: MemoryRow) => void; onSelect: (row: MemoryRow) => void }) {
  const date = row.mentioned_at || row.occurred_start
  return (
    <button type="button" onPointerEnter={() => onPreview(row)} onFocus={() => onPreview(row)} onClick={() => onSelect(row)} className="group block w-full rounded-lg border border-transparent p-3 text-left transition-colors hover:border-border hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
      {typeof row.score === 'number' && (
        <span className="mb-1.5 block text-right font-mono text-[10.5px] tabular-nums text-faint-foreground">{copy.inspector.score} {row.score.toFixed(3)}</span>
      )}
      <span className="line-clamp-3 block text-xs leading-5 text-foreground/85">{row.text}</span>
      {date && <span className="mt-2 flex items-center gap-1 text-[10.5px] text-faint-foreground"><CalendarClock className="size-3" />{formatMemoryDate(date)}</span>}
    </button>
  )
}

function EmptyRecall({ title, description }: { title: string; description: string }) {
  return (
    <motion.div className="flex min-h-72 flex-col items-center justify-center px-6 text-center" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24, ease: 'easeOut' }}>
      <span className="relative mb-4 grid size-11 place-items-center rounded-2xl border border-primary/15 bg-primary/[0.06] text-primary shadow-sm">
        <span className="absolute inset-1 rounded-xl border border-primary/10" />
        <ScanSearch className="relative size-5" />
      </span>
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1.5 max-w-64 text-xs leading-5 text-muted-foreground">{description}</p>
    </motion.div>
  )
}

function SearchStatus({ icon, title }: { icon: ReactNode; title: string }) {
  return <div className="flex min-h-52 items-center justify-center gap-2.5 text-xs text-muted-foreground">{icon}<span>{title}</span></div>
}
